import React, { createContext, useState, useContext, useCallback } from 'react';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TransitionContextType {
  targetRect: Rect | null;
  isTransitioning: boolean;
  isTransitionComplete: boolean;
  setTargetRect: (rect: Rect | null) => void;
  startTransition: () => void;
  endTransition: () => void;
}

const TransitionContext = createContext<TransitionContextType | undefined>(undefined);

export const TransitionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [targetRect, setTargetRect] = useState<Rect | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isTransitionComplete, setIsTransitionComplete] = useState(false);

  const startTransition = useCallback(() => {
    setIsTransitioning(true);
    setIsTransitionComplete(false);
  }, []);

  const endTransition = useCallback(() => {
    setIsTransitioning(false);
    setIsTransitionComplete(true);
  }, []);

  return (
    <TransitionContext.Provider 
      value={{ 
        targetRect, 
        isTransitioning, 
        isTransitionComplete,
        setTargetRect, 
        startTransition, 
        endTransition 
      }}
    >
      {children}
    </TransitionContext.Provider>
  );
};

export const useTransition = () => {
  const context = useContext(TransitionContext);
  if (!context) {
    throw new Error('useTransition must be used within a TransitionProvider');
  }
  return context;
};
